import { useEffect, useState, useCallback } from "react";
import { Download, Trash2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, todayISO } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CorrectionDialog, tzTime } from "../components/CorrectionDialog";

const monthStart = () => todayISO().slice(0, 8) + "01";
const inputCls = "h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-slate-900";

export default function Records() {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayISO());
  const [worker, setWorker] = useState("all");
  const [workers, setWorkers] = useState([]);
  const [rows, setRows] = useState([]);
  const [tz, setTz] = useState("UTC");
  const [dlg, setDlg] = useState({ open: false, record: null });

  const params = useCallback(() => ({ start, end, ...(worker !== "all" ? { worker_id: worker } : {}) }), [start, end, worker]);
  const load = useCallback(() => api.get("/attendance", { params: params() }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [params]);

  useEffect(() => {
    api.get("/workers").then((r) => setWorkers(r.data)).catch(() => {});
    api.get("/public/info").then((r) => setTz(r.data.timezone)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/attendance/export", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `attendance_${start}_${end}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.worker_name}'s record for ${r.date}?`)) return;
    try { await api.delete(`/attendance/${r.id}`); toast.success("Record deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHeader overline={`${rows.length} records`} title="Attendance Records">
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="records-date-filter-start" className={inputCls} />
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="records-date-filter-end" className={inputCls} />
        <Select value={worker} onValueChange={setWorker}>
          <SelectTrigger className="w-48 h-10 bg-white" data-testid="records-worker-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workers</SelectItem>
            {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => setDlg({ open: true, record: null })} data-testid="records-add-manual-btn" className="h-10 px-5 rounded-full bg-slate-900 hover:bg-emerald-500 hover:text-slate-950 text-white text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-colors">
          <Plus size={16} /> Manual entry
        </button>
        <button onClick={exportCsv} data-testid="records-csv-export-btn" className="h-10 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-colors">
          <Download size={16} /> Export CSV
        </button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="records-table">
          <thead className="text-left text-xs uppercase text-slate-500 font-mono bg-slate-50">
            <tr><th className="px-5 py-3">Date</th><th className="px-5 py-3">Worker</th><th className="px-5 py-3">In</th><th className="px-5 py-3">Out</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Hours</th><th className="px-5 py-3" /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No records in this range.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`record-row-${r.id}`}>
                <td className="px-5 py-3 font-mono">{r.date}</td>
                <td className="px-5 py-3"><p className="font-medium">{r.worker_name}</p><p className="font-mono text-xs text-slate-400">{r.worker_code}</p></td>
                <td className="px-5 py-3 font-mono">{r.check_in ? tzTime(r.check_in, tz) : "—"}</td>
                <td className="px-5 py-3 font-mono">{r.check_out ? tzTime(r.check_out, tz) : <span className="text-amber-600 text-xs font-bold">MISSING</span>}</td>
                <td className="px-5 py-3"><StatusBadge status={r.status} />{r.late_minutes > 0 && <span className="ml-2 text-xs text-amber-600 font-mono">+{r.late_minutes}m</span>}
                  {r.corrected && <span title={r.note || "Corrected by admin"} data-testid={`record-corrected-badge-${r.id}`} className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-300 text-[10px] font-mono font-bold text-slate-600">EDITED</span>}
                  {r.note && <p className="text-xs text-slate-400 mt-1 max-w-[220px] truncate">{r.note}</p>}</td>
                <td className="px-5 py-3 font-mono">{r.check_out ? r.hours : "—"}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap"><button onClick={() => setDlg({ open: true, record: r })} data-testid={`record-edit-btn-${r.id}`} className="p-2 rounded-md hover:bg-slate-200 transition-colors" title="Correct"><Pencil size={14} /></button><button onClick={() => remove(r)} data-testid={`record-delete-btn-${r.id}`} className="p-2 rounded-md hover:bg-rose-100 text-rose-600 transition-colors"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CorrectionDialog open={dlg.open} onOpenChange={(v) => setDlg({ ...dlg, open: v })} record={dlg.record} workers={workers} tz={tz} onSaved={load} />
    </>
  );
}
